import Vue from 'vue';
import constants from '@/shared/constants/constants';
import { normalizePosterIndex } from '@/shared/helpers/generationHistory';
import {
  fetchGenerationHistoryDetail,
  fetchGenerationHistoryFavoriteStatus,
  fetchGenerationHistoryFavorites,
  fetchGenerationHistoryList,
  toggleGenerationHistoryFavorite,
} from '@/services/generationHistory.service';

const state = {
  isOpen: false,
  isExpanded: true,
  activeTab: 'history',
  view: 'list',
  filter: constants.GENERATION_HISTORY_FILTER_ALL,
  keyword: '',
  listItems: [],
  listTotal: 0,
  listPage: 1,
  listPageSize: 20,
  listLoading: false,
  listError: '',
  listRequestId: 0,
  listScrollTop: 0,
  scrollAfterRefresh: null,
  detailProjectId: null,
  detail: null,
  detailLoading: false,
  detailError: '',
  // Cross-screen sync: `${item_type}-${item_id}-${poster_index}` → boolean
  // Kept when sidebar closes so generate screens stay in sync.
  favoriteFlags: {},
};

function favoriteFlagKey(itemType, itemId, posterIndex = 0) {
  return `${itemType}-${itemId}-${normalizePosterIndex(posterIndex)}`;
}

const mutations = {
  GH_SET_OPEN(s, value) {
    s.isOpen = !!value;
  },
  GH_SET_EXPANDED(s, value) {
    s.isExpanded = !!value;
  },
  GH_SET_ACTIVE_TAB(s, tab) {
    s.activeTab = tab;
  },
  GH_SET_VIEW(s, view) {
    s.view = view;
  },
  GH_SET_FILTER(s, payload) {
    s.filter = payload.filter;
    s.keyword = payload.keyword || '';
  },
  GH_RESET_LIST(s) {
    s.listItems = [];
    s.listTotal = 0;
    s.listPage = 1;
    s.listError = '';
    s.listScrollTop = 0;
  },
  GH_SET_LIST_SCROLL_TOP(s, scrollTop) {
    s.listScrollTop = Math.max(0, Number(scrollTop) || 0);
  },
  GH_SET_SCROLL_AFTER_REFRESH(s, value) {
    s.scrollAfterRefresh = value || null;
  },
  GH_APPEND_LIST(s, payload) {
    const sortField = payload.sortField || 'request_datetime_ts';
    // History is grouped by project; favorites are per item (+ poster slot for concept).
    // Key by tab, not by whether item_type/item_id happen to be present on the row.
    const itemKey = (row) => {
      if (s.activeTab === 'favorites') {
        const posterIndex = row.poster_index != null ? normalizePosterIndex(row.poster_index) : 0;
        return `${row.item_type}-${row.item_id}-${posterIndex}`;
      }
      return `project-${row.project_id}`;
    };
    const sortByNewest = (items) => [...items].sort(
      (a, b) => (b[sortField] || 0) - (a[sortField] || 0),
    );
    const uniqueByItem = (items) => {
      const seen = new Set();
      return items.filter((row) => {
        const key = itemKey(row);
        if (seen.has(key)) {
          return false;
        }
        seen.add(key);
        return true;
      });
    };
    const incomingItems = sortByNewest(payload.items || []);
    s.listItems = payload.replace
      ? incomingItems
      : sortByNewest(uniqueByItem(s.listItems.concat(incomingItems)));
    if (!payload.replace && !incomingItems.length) {
      // Empty page after client-side removals — stop infinite load-more.
      s.listTotal = s.listItems.length;
    } else {
      s.listTotal = payload.total;
    }
    s.listPage = payload.page;
  },
  GH_SET_LIST_LOADING(s, value) {
    s.listLoading = value;
  },
  GH_SET_LIST_ERROR(s, message) {
    s.listError = message || '';
  },
  GH_SET_LIST_REQUEST_ID(s, requestId) {
    s.listRequestId = requestId;
  },
  GH_SET_DETAIL_PROJECT_ID(s, projectId) {
    s.detailProjectId = projectId;
  },
  GH_SET_DETAIL(s, detail) {
    s.detail = detail;
  },
  GH_SET_DETAIL_LOADING(s, value) {
    s.detailLoading = value;
  },
  GH_SET_DETAIL_ERROR(s, message) {
    s.detailError = message || '';
  },
  GH_UPDATE_CARD_FAVORITE(s, payload) {
    const posterIndex = payload.poster_index != null ? normalizePosterIndex(payload.poster_index) : 0;
    Vue.set(
      s.favoriteFlags,
      favoriteFlagKey(payload.item_type, payload.item_id, posterIndex),
      !!payload.is_favorite,
    );
    if (!s.detail || !s.detail.cards) {
      return;
    }
    const card = s.detail.cards.find((row) => (
      row.item_type === payload.item_type && row.item_id === payload.item_id
    ));
    if (!card) {
      return;
    }
    const currentIndexes = Array.isArray(card.favorite_poster_indexes)
      ? card.favorite_poster_indexes.map((value) => normalizePosterIndex(value))
      : [];
    let nextIndexes;
    if (payload.is_favorite) {
      nextIndexes = currentIndexes.includes(posterIndex)
        ? currentIndexes
        : currentIndexes.concat(posterIndex).sort((a, b) => a - b);
    } else {
      nextIndexes = currentIndexes.filter((value) => value !== posterIndex);
    }
    Vue.set(card, 'favorite_poster_indexes', nextIndexes);
    Vue.set(card, 'is_favorite', nextIndexes.length > 0);
  },
  GH_SET_FAVORITE_FLAGS(s, items) {
    (items || []).forEach((row) => {
      const posterIndex = row.poster_index != null ? normalizePosterIndex(row.poster_index) : 0;
      Vue.set(
        s.favoriteFlags,
        favoriteFlagKey(row.item_type, row.item_id, posterIndex),
        !!row.is_favorite,
      );
    });
  },
  GH_REMOVE_FAVORITE_ITEM(s, payload) {
    const posterIndex = payload.poster_index != null ? normalizePosterIndex(payload.poster_index) : 0;
    s.listItems = s.listItems.filter((row) => {
      if (row.item_type !== payload.item_type || row.item_id !== payload.item_id) {
        return true;
      }
      const rowPosterIndex = row.poster_index != null ? normalizePosterIndex(row.poster_index) : 0;
      return rowPosterIndex !== posterIndex;
    });
    s.listTotal = Math.max(0, s.listTotal - 1);
  },
  GH_RESET_STATE(s) {
    s.isOpen = false;
    s.isExpanded = true;
    s.activeTab = 'history';
    s.view = 'list';
    s.filter = constants.GENERATION_HISTORY_FILTER_ALL;
    s.keyword = '';
    s.listItems = [];
    s.listTotal = 0;
    s.listPage = 1;
    s.listError = '';
    s.listRequestId = 0;
    s.listScrollTop = 0;
    s.scrollAfterRefresh = null;
    s.detailProjectId = null;
    s.detail = null;
    s.detailLoading = false;
    s.detailError = '';
  },
};

const actions = {
  openSidebar({ commit, dispatch }, payload = {}) {
    const tab = payload.tab || 'history';
    commit('GH_SET_OPEN', true);
    commit('GH_SET_EXPANDED', true);
    commit('GH_SET_ACTIVE_TAB', tab);
    commit('GH_SET_VIEW', 'list');
    commit('GH_SET_FILTER', {
      filter: payload.filter != null ? payload.filter : constants.GENERATION_HISTORY_FILTER_ALL,
      keyword: payload.keyword || '',
    });
    commit('GH_RESET_LIST');
    commit('GH_SET_DETAIL_PROJECT_ID', null);
    commit('GH_SET_DETAIL', null);
    if (tab === 'favorites') {
      dispatch('fetchFavoritesList', { page: 1, replace: true });
    } else {
      dispatch('fetchList', { page: 1, replace: true });
    }
  },
  closeSidebar({ commit }) {
    commit('GH_RESET_STATE');
  },
  toggleExpanded({ commit, state: s }) {
    commit('GH_SET_EXPANDED', !s.isExpanded);
  },
  setActiveTab({ commit, dispatch, state: s }, tab) {
    if (s.activeTab === tab) {
      return;
    }
    commit('GH_SET_ACTIVE_TAB', tab);
    commit('GH_RESET_LIST');
    // RECENT_30D is tab-scoped (Recent Generations / Recent Favorites) — do not carry over
    if (s.filter === constants.GENERATION_HISTORY_FILTER_RECENT_30D) {
      commit('GH_SET_FILTER', {
        filter: constants.GENERATION_HISTORY_FILTER_ALL,
        keyword: '',
      });
    }
    if (tab === 'favorites') {
      dispatch('fetchFavoritesList', { page: 1, replace: true });
    } else {
      dispatch('fetchList', { page: 1, replace: true });
    }
  },
  async fetchList({ commit, state: s }, { page = 1, replace = false } = {}) {
    const requestId = s.listRequestId + 1;
    commit('GH_SET_LIST_REQUEST_ID', requestId);
    commit('GH_SET_LIST_LOADING', true);
    commit('GH_SET_LIST_ERROR', '');
    const params = {
      filter: s.filter,
      page,
      page_size: s.listPageSize,
    };
    if (s.filter === constants.GENERATION_HISTORY_FILTER_KEYWORD && s.keyword) {
      params.keyword = s.keyword;
    }
    try {
      const { status, data } = await fetchGenerationHistoryList(params);
      if (requestId !== s.listRequestId) {
        return;
      }
      if (status !== constants.HTTP_OK) {
        const message = (data && data.message) || '';
        commit('GH_SET_LIST_ERROR', message);
        if (replace) {
          commit('GH_RESET_LIST');
        }
        return;
      }
      commit('GH_APPEND_LIST', {
        items: data.results || [],
        total: data.count || 0,
        page: data.page || page,
        replace,
      });
    } finally {
      if (requestId === s.listRequestId) {
        commit('GH_SET_LIST_LOADING', false);
      }
    }
  },
  async fetchFavoritesList({ commit, state: s }, { page = 1, replace = false } = {}) {
    const requestId = s.listRequestId + 1;
    commit('GH_SET_LIST_REQUEST_ID', requestId);
    commit('GH_SET_LIST_LOADING', true);
    commit('GH_SET_LIST_ERROR', '');
    // Favorites API only supports ALL / RECENT_30D
    const favoritesFilters = [
      constants.GENERATION_HISTORY_FILTER_ALL,
      constants.GENERATION_HISTORY_FILTER_RECENT_30D,
    ];
    const filter = favoritesFilters.includes(s.filter)
      ? s.filter
      : constants.GENERATION_HISTORY_FILTER_ALL;
    const params = {
      filter,
      page,
      page_size: s.listPageSize,
    };
    try {
      const response = await fetchGenerationHistoryFavorites(params);
      if (requestId !== s.listRequestId) {
        return;
      }
      if (!response || response.status !== constants.HTTP_OK) {
        const message = (response && response.data && response.data.message) || '';
        commit('GH_SET_LIST_ERROR', message);
        if (replace) {
          commit('GH_RESET_LIST');
        }
        return;
      }
      commit('GH_APPEND_LIST', {
        items: response.data.results || [],
        total: response.data.count || 0,
        page: response.data.page || page,
        replace,
      });
    } finally {
      if (requestId === s.listRequestId) {
        commit('GH_SET_LIST_LOADING', false);
      }
    }
  },
  saveListScrollTop({ commit }, scrollTop) {
    commit('GH_SET_LIST_SCROLL_TOP', scrollTop);
  },
  clearScrollAfterRefresh({ commit }) {
    commit('GH_SET_SCROLL_AFTER_REFRESH', null);
  },
  loadMoreList({ dispatch, state: s }) {
    if (s.listLoading || s.listItems.length >= s.listTotal) {
      return;
    }
    const nextPage = s.listPage + 1;
    if (s.activeTab === 'favorites') {
      dispatch('fetchFavoritesList', { page: nextPage, replace: false });
    } else {
      dispatch('fetchList', { page: nextPage, replace: false });
    }
  },
  async openDetail({ commit, dispatch }, projectId) {
    commit('GH_SET_VIEW', 'detail');
    commit('GH_SET_DETAIL_PROJECT_ID', projectId);
    commit('GH_SET_DETAIL', null);
    commit('GH_SET_DETAIL_LOADING', true);
    commit('GH_SET_DETAIL_ERROR', '');
    const detailLoadError = '詳細の読み込みに失敗しました。';
    try {
      const response = await fetchGenerationHistoryDetail(projectId);
      const status = response && response.status;
      const data = response && response.data;
      if (status !== constants.HTTP_OK) {
        commit('GH_SET_DETAIL_ERROR', (data && data.message) || detailLoadError);
        commit('GH_SET_SCROLL_AFTER_REFRESH', null);
        return;
      }
      commit('GH_SET_DETAIL', data);
    } catch (e) {
      commit('GH_SET_DETAIL_ERROR', detailLoadError);
      commit('GH_SET_SCROLL_AFTER_REFRESH', null);
    } finally {
      commit('GH_SET_DETAIL_LOADING', false);
    }
  },
  backToList({ commit, dispatch, state: s }) {
    commit('GH_SET_VIEW', 'list');
    commit('GH_SET_DETAIL_PROJECT_ID', null);
    commit('GH_SET_DETAIL', null);
    commit('GH_SET_DETAIL_ERROR', '');
    if (s.activeTab === 'favorites') {
      dispatch('fetchFavoritesList', { page: 1, replace: true });
    } else {
      dispatch('fetchList', { page: 1, replace: true });
    }
  },
  async toggleFavorite({ commit }, payload) {
    const posterIndex = payload.poster_index != null ? normalizePosterIndex(payload.poster_index) : 0;
    const response = await toggleGenerationHistoryFavorite({
      ...payload,
      poster_index: posterIndex,
    });
    if (!response || response.status !== constants.HTTP_OK) {
      return {
        success: false,
        message: (response && response.data && response.data.message) || '',
      };
    }
    commit('GH_UPDATE_CARD_FAVORITE', {
      item_type: payload.item_type,
      item_id: payload.item_id,
      poster_index: posterIndex,
      is_favorite: response.data.is_favorite,
    });
    return {
      success: true,
      is_favorite: response.data.is_favorite,
      poster_index: posterIndex,
    };
  },
  /**
   * Seed favoriteFlags from server without mutating detail cards.
   * Silent no-op on empty items or API failure (MVP).
   */
  async hydrateFavoriteFlags({ commit }, { items } = {}) {
    const deduped = [];
    const seen = new Set();
    (items || []).forEach((row) => {
      if (row == null || row.item_type == null || row.item_id == null) {
        return;
      }
      const posterIndex = row.poster_index != null ? normalizePosterIndex(row.poster_index) : 0;
      const key = favoriteFlagKey(row.item_type, row.item_id, posterIndex);
      if (seen.has(key)) {
        return;
      }
      seen.add(key);
      deduped.push({
        item_type: row.item_type,
        item_id: row.item_id,
        poster_index: posterIndex,
      });
    });
    if (!deduped.length) {
      return;
    }
    const response = await fetchGenerationHistoryFavoriteStatus(deduped);
    if (!response || response.status !== constants.HTTP_OK || !response.data) {
      return;
    }
    commit('GH_SET_FAVORITE_FLAGS', response.data.items || []);
  },
  /**
   * After favoriting from a generate screen: if favorites list is open, scroll top + refetch.
   * Does not run for in-list unfavorite animation (callers outside the favorites list only).
   */
  async refreshFavoritesListAfterExternalToggle({ commit, dispatch, state: s }) {
    if (!s.isOpen || s.activeTab !== 'favorites' || s.view !== 'list') {
      return;
    }
    commit('GH_SET_LIST_SCROLL_TOP', 0);
    await dispatch('fetchFavoritesList', { page: 1, replace: true });
    commit('GH_SET_SCROLL_AFTER_REFRESH', 'list-top');
  },
  async removeFavoriteItem({ commit, dispatch, state: s }, payload) {
    commit('GH_REMOVE_FAVORITE_ITEM', payload);
    const expectedCount = Math.min(s.listTotal, s.listPage * s.listPageSize);
    if (s.listItems.length >= expectedCount) {
      return;
    }
    // Backfill the slot that shifted into the current loaded window after unfavorite.
    await dispatch('fetchFavoritesList', { page: s.listPage, replace: false });
  },
  /**
   * Refresh open sidebar after a new generate job is created / finishes.
   * No-op when sidebar is closed (next openSidebar already fetches fresh data).
   * Scroll (list top / detail bottom) only when `scroll: true` (generate succeeded).
   */
  async refreshAfterGenerate({ commit, dispatch, state: s }, { projectId, scroll = false } = {}) {
    if (!s.isOpen) {
      return;
    }
    const onSameProjectDetail = (
      s.view === 'detail'
      && projectId
      && Number(s.detailProjectId) === Number(projectId)
    );
    // Create-time refresh while already on same project detail: skip list fetch;
    // SUCCESS path refreshes detail + scrolls.
    if (!scroll && onSameProjectDetail) {
      await dispatch('openDetail', projectId);
      return;
    }
    if (s.activeTab === 'history') {
      if (scroll && s.view === 'list') {
        commit('GH_SET_LIST_SCROLL_TOP', 0);
      }
      await dispatch('fetchList', { page: 1, replace: true });
    } else if (s.activeTab === 'favorites') {
      // Poster regenerate clears poster favorite on BE — refresh favorites list in place.
      if (scroll && s.view === 'list') {
        commit('GH_SET_LIST_SCROLL_TOP', 0);
      }
      await dispatch('fetchFavoritesList', { page: 1, replace: true });
    }
    if (onSameProjectDetail) {
      await dispatch('openDetail', projectId);
      // Only scroll when detail loaded successfully; otherwise flag would stick
      // (watchers return early on null detail / detailError and never clear it).
      if (scroll && s.detail && !s.detailError) {
        commit('GH_SET_SCROLL_AFTER_REFRESH', 'detail-bottom');
      }
      return;
    }
    if (scroll && s.view === 'list') {
      commit('GH_SET_SCROLL_AFTER_REFRESH', 'list-top');
    }
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
};
